// src/pages/Usuarios.jsx
import React, { useEffect, useState } from "react";
import "./Usuarios.css";
import { authFetch } from "../utils/authFetch";
import {
  Boton, EncabezadoPagina, Iconos, Insignia, Tarjeta,
} from "../componentes/base";

export default function Usuarios() {
  const API = "/api/usuarios";

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===== NUEVO USUARIO =====
  const [nuevo, setNuevo] = useState({
    usuario: "",
    nombre: "",
    password: "",
    rol: "tecnicos",
  });

  // ===== RESET PASSWORD =====
  const [reset, setReset] = useState({ id: null, password: "" });

  function toast(mensaje, tipo = "info") {
    window.dispatchEvent(
      new CustomEvent("toast", { detail: { mensaje, tipo } })
    );
  }

  async function cargar() {
    setLoading(true);
    try {
      const res = await authFetch(API);
      const data = await res.json();
      if (data.ok) setUsuarios(data.usuarios || []);
      else toast(data.mensaje || "Error cargando usuarios", "error");
    } catch {
      toast("Error de red cargando usuarios", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line
  }, []);

  function onNuevo(campo, valor) {
    setNuevo((p) => ({ ...p, [campo]: valor }));
  }

  // ============================================================
  // CREAR USUARIO  ✅ (password plano → bcrypt en backend)
  // ============================================================
  async function crearUsuario() {
    if (!nuevo.usuario || !nuevo.password) {
      toast("Usuario y password son obligatorios", "info");
      return;
    }

    try {
      const res = await authFetch(`${API}/nuevo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usuario: nuevo.usuario.trim(),
          nombre: (nuevo.nombre || "").trim(),
          rol: nuevo.rol,
          password: nuevo.password, // ✅ CORRECTO
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        toast(data.mensaje || "No se pudo crear", "error");
        return;
      }

      toast("Usuario creado", "success");
      setNuevo({ usuario: "", nombre: "", password: "", rol: "tecnicos" });
      cargar();
    } catch {
      toast("Error de red creando usuario", "error");
    }
  }

  // ============================================================
  // EDITAR CAMPOS (rol / activo / nombre)
  // ============================================================
  async function setCampo(id, campo, valor) {
    try {
      const res = await authFetch(`${API}/editar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, campo, valor }),
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        toast(data.mensaje || "No se pudo actualizar", "error");
        return;
      }

      toast("Actualizado", "success");
      setUsuarios((prev) =>
        prev.map((u) => (u.id === id ? { ...u, [campo]: valor } : u))
      );
    } catch {
      toast("Error de red actualizando", "error");
    }
  }

  // ============================================================
  // RESET PASSWORD  ✅ (password plano → bcrypt en backend)
  // ============================================================
  async function resetPassword() {
    if (!reset.id || !reset.password) {
      toast("Selecciona usuario y escribe nueva contraseña", "info");
      return;
    }

    try {
      const res = await authFetch(`${API}/reset_password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: reset.id,
          password: reset.password, // ✅ CORRECTO
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        toast(data.mensaje || "No se pudo resetear", "error");
        return;
      }

      toast("Password actualizado", "success");
      setReset({ id: null, password: "" });
    } catch {
      toast("Error de red reseteando password", "error");
    }
  }

  return (
    <div className="usuarios-page">
      <EncabezadoPagina
        icono={Iconos.usuarios}
        titulo="Usuarios"
        resumen={`${usuarios.length} con acceso al sistema`}
        acciones={
          <Boton
            variante="secundario"
            icono={Iconos.actualizar}
            cargando={loading}
            onClick={cargar}
            type="button"
          >
            Actualizar
          </Boton>
        }
      />

      {/* ===== CREAR USUARIO ===== */}
      <Tarjeta titulo="Crear usuario">
        <div className="usuarios-form">
          <input
            className="usuarios-inp"
            placeholder="Usuario (login)"
            value={nuevo.usuario}
            onChange={(e) => onNuevo("usuario", e.target.value)}
          />
          <input
            className="usuarios-inp"
            placeholder="Nombre"
            value={nuevo.nombre}
            onChange={(e) => onNuevo("nombre", e.target.value)}
          />
          <input
            className="usuarios-inp"
            placeholder="Password"
            type="password"
            value={nuevo.password}
            onChange={(e) => onNuevo("password", e.target.value)}
          />
          <select
            className="usuarios-inp"
            value={nuevo.rol}
            onChange={(e) => onNuevo("rol", e.target.value)}
          >
            <option value="admin">admin</option>
            <option value="caja">caja</option>
            <option value="tecnicos">tecnicos</option>
          </select>

          <Boton
            icono={Iconos.agregar}
            onClick={crearUsuario}
            type="button"
            disabled={loading}
          >
            Crear
          </Boton>
        </div>
      </Tarjeta>

      {/* ===== RESET PASSWORD ===== */}
      <Tarjeta titulo="Cambiar contraseña">
        <div className="usuarios-form usuarios-form-reset">
          <select
            className="usuarios-inp"
            value={reset.id ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setReset((p) => ({ ...p, id: v ? Number(v) : null }));
            }}
          >
            <option value="">Selecciona usuario</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                #{u.id} - {u.usuario} ({u.rol})
              </option>
            ))}
          </select>

          <input
            className="usuarios-inp"
            type="password"
            placeholder="Nuevo password"
            value={reset.password}
            onChange={(e) =>
              setReset((p) => ({ ...p, password: e.target.value }))
            }
          />

          <Boton
            icono={Iconos.confirmar}
            onClick={resetPassword}
            type="button"
            disabled={loading}
          >
            Cambiar
          </Boton>
        </div>
      </Tarjeta>

      {/* ===== LISTA ===== */}
      <Tarjeta
        titulo="Lista"
        acciones={<Insignia variante="neutra" texto={`${usuarios.length} usuarios`} />}
        sinEspacio
      >
        <div className="usuarios-table-wrap">
          <table className="usuarios-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Activo</th>
                <th>Creado</th>
              </tr>
            </thead>

            <tbody>
              {usuarios.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    Sin usuarios
                  </td>
                </tr>
              ) : (
                usuarios.map((u) => (
                  <tr key={u.id}>
                    <td data-label="ID">{u.id}</td>
                    <td data-label="Usuario">{u.usuario}</td>

                    <td data-label="Nombre">
                      <input
                        className="cell-input"
                        value={u.nombre || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setUsuarios((prev) =>
                            prev.map((x) =>
                              x.id === u.id ? { ...x, nombre: v } : x
                            )
                          );
                        }}
                        onBlur={(e) =>
                          setCampo(u.id, "nombre", e.target.value)
                        }
                      />
                    </td>

                    <td data-label="Rol">
                      <select
                        className="cell-input"
                        value={u.rol}
                        onChange={(e) =>
                          setCampo(u.id, "rol", e.target.value)
                        }
                      >
                        <option value="admin">admin</option>
                        <option value="caja">caja</option>
                        <option value="tecnicos">tecnicos</option>
                      </select>
                    </td>

                    <td data-label="Activo">
                      {/* Es una insignia que además se pulsa para alternar.
                          El `title` lo explica: el color solo dice cómo
                          está, no que se pueda cambiar desde ahí. */}
                      <button
                        className="usuarios-toggle"
                        onClick={() =>
                          setCampo(u.id, "activo", u.activo ? 0 : 1)
                        }
                        type="button"
                        title={u.activo ? "Desactivar este usuario" : "Activar este usuario"}
                      >
                        <Insignia
                          variante={u.activo ? "activo" : "cancelado"}
                          icono={u.activo ? Iconos.activo : Iconos.cancelado}
                          texto={u.activo ? "Activo" : "Inactivo"}
                        />
                      </button>
                    </td>

                    <td data-label="Creado">
                      {u.creado
                        ? new Date(u.creado).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Tarjeta>
    </div>
  );
}
